import React, { useEffect } from 'react';
import { Button } from 'components/form/Button/index';
import { FlexContainer } from 'components/Containers/FlexContainer/FlexContainer';
import drawGradeDonut from '../../helpers/drawGradeDonut';
import { isFirstTimeCreditUser } from 'helpers/tuCreditReport.helper';
import '../../style/EngagementModule.scss';
import {
  PAST_DUE_TRADE_LINE,
  PAST_DUE_TO_CURRENT,
  SCORE_CHANGE,
  NEW_TRADE_LINES,
  DECLINED_TRADE_LINES,
  GRADE_CHANGE,
  POTENTIAL_SCORE,
  PAYMENT_STREAK_TRADE_LINES,
  MISSED_PAYMENT_TRADE_LINE,
  EventTypeToPopupNameMap,
  EventWithConfetti,
  EventWithConfettiPositive,
} from 'types/engagementModule.eventType.constants';
import { EVENT_ENGAGEMENT } from 'types/morph.pagePosition.constants';
import { FACTOR_ENUMS_ARRAY } from '../../GradeDialController';
import { FACTOR_ENUMS } from 'pages/RedesignDashboard/constants';
import { useEventHandler } from 'hook/event.hook';
import {
  VIEW_POPUP,
  CLICK,
} from 'externals/_tracking/types/eventTypes.constants';
import { setShouldShowExpandedEngagementModule } from 'helpers/sessionstorage';
import { Lottie } from 'components/Lottie/Lottie';
import { CSTooltip } from 'components/Tooltip/CSToolTip';
import { QuestionCircleOutlined } from '@ant-design/icons';

const CreditFactorChart = ({
  className,
  data,
  csGrade,
  clickDonut,
  donutRef,
  isSomeFactorClicked,
  selectedFactor,
  isPotentialView,
  handleClickPotential,
  handleGoToCreditReports,
  clickable = true,
  potentialCreditProfileContainerError,
  handleTryAgainPotentialCreditProfile,
  showEngagement,
  setShowEngagement,
  eventEngagementData = {},
  history,
  showEngagementModuleFromFlag,
  config,
}) => {
  const eventHandler = useEventHandler();
  useEffect(() => {
    if (donutRef.current) {
      drawGradeDonut(
        donutRef.current,
        data,
        csGrade,
        clickDonut,
        isSomeFactorClicked,
        selectedFactor,
        isPotentialView,
        clickable,
        potentialCreditProfileContainerError,
        handleTryAgainPotentialCreditProfile,
        setShowEngagement,
        showEngagement,
        eventEngagementData,
        showEngagementModuleFromFlag,
        handleGoToCreditReports,
        config,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    donutRef,
    selectedFactor,
    isSomeFactorClicked,
    isPotentialView,
    data,
    csGrade,
    handleTryAgainPotentialCreditProfile,
    setShowEngagement,
    showEngagement,
    eventEngagementData,
    showEngagementModuleFromFlag,
  ]);

  const {
    titleText = 'Action Needed for Credit Health',
    subtitleText = 'Take action now to minimize any impact to your credit score. Timely steps can make a big difference!',
    ctaText = 'Act Now',
    positive = false,
    eventType = '',
    eventData = {},
    creditFactor,
  } = eventEngagementData;

  const getPopupName = () => {
    if (eventType === GRADE_CHANGE) {
      return `${EventTypeToPopupNameMap[GRADE_CHANGE]}${creditFactor}`;
    }
    return EventTypeToPopupNameMap[eventType] || '';
  };

  const hasConfetti = () => {
    if (EventWithConfetti.includes(eventType)) {
      return true;
    } else if (EventWithConfettiPositive.includes(eventType)) {
      return positive;
    } else {
      return false;
    }
  };

  const viewPageAttributes = {
    'Pop Up Name': getPopupName(),
  };

  useEffect(() => {
    if (showEngagement && showEngagementModuleFromFlag) {
      eventHandler(VIEW_POPUP, viewPageAttributes);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showEngagement, showEngagementModuleFromFlag]);

  const ctaHandler = () => {
    const ctaClickAttributes = {
      'Click Type': ctaText,
      'Module Name': EVENT_ENGAGEMENT,
    };

    switch (eventType) {
      case PAST_DUE_TRADE_LINE:
      case PAST_DUE_TO_CURRENT:
        history.push('/alerts/all');
        break;
      case SCORE_CHANGE:
        history.push('/creditReports/tabs?nav=premiumCreditReports');
        break;
      case NEW_TRADE_LINES:
        clickDonut(FACTOR_ENUMS_ARRAY.indexOf(FACTOR_ENUMS.ACCOUNT_MIX) + 1);
        break;
      case DECLINED_TRADE_LINES:
        history.push('/goals/actions');
        break;
      case GRADE_CHANGE:
        clickDonut(FACTOR_ENUMS_ARRAY.indexOf(creditFactor) + 1);
        break;
      case POTENTIAL_SCORE:
        handleClickPotential();
        if (eventData?.scoreDifference === 0) {
          ctaClickAttributes['Click Type'] =
            'Engagement - No Potential Improve';
        }
        break;
      case PAYMENT_STREAK_TRADE_LINES:
        clickDonut(
          FACTOR_ENUMS_ARRAY.indexOf(FACTOR_ENUMS.PAYMENT_HISTORY) + 1,
        );
        break;
      case MISSED_PAYMENT_TRADE_LINE:
        clickDonut(
          FACTOR_ENUMS_ARRAY.indexOf(FACTOR_ENUMS.PAYMENT_HISTORY) + 1,
        );
        break;
      default: {
        break;
      }
    }
    setShowEngagement(false);

    eventHandler(CLICK, ctaClickAttributes);
  };

  return (
    <div className={className}>
      <div className="credit-factor-graph" ref={donutRef}>
        {!isSomeFactorClicked &&
          showEngagement &&
          showEngagementModuleFromFlag && (
            <div>
              <div
                className="engagementModal-mask"
                onClick={() => {
                  setShowEngagement(false);
                  setShouldShowExpandedEngagementModule(false);
                }}
              ></div>
              {hasConfetti() && (
                <Lottie
                  autoplay={true}
                  loop={true}
                  style={{ height: '100%', width: '100%', top: 0, left: 0 }}
                  src={'/images/lottie/pre-qual-confetti.json'}
                  className="pos-abs"
                />
              )}
              <FlexContainer className={'flex-column'}>
                <div className="engagementModal">
                  <div className="engagementTitle text-14 text-center margLR24 margT24 margB16">
                    {titleText}
                  </div>
                  <div className="text-12 margB16 text-center padLR24">
                    {subtitleText}
                  </div>
                  <Button
                    mrphType="primary"
                    className={'engagementBtn margB24 text-14'}
                    onClick={ctaHandler}
                  >
                    {ctaText}
                  </Button>
                </div>
                <div
                  className="half-circle margT24"
                  onClick={() => {
                    setShowEngagement(false);
                    setShouldShowExpandedEngagementModule(false);
                  }}
                >
                  <div className="circle-image"></div>
                </div>
              </FlexContainer>
            </div>
          )}
      </div>
      {config.dashboardStyle.gradeDial.showPoorUserTooltip &&
        isFirstTimeCreditUser(csGrade?.score) && (
          <FlexContainer>
            <div className="poorUser">
              <CSTooltip
                placement="bottom"
                overlayClassName="blueTooltip"
                tooltipContent={
                  <>
                    "Limited Credit History" means that your credit report may
                    not have enough information to generate a VantageScore® 3.0
                    credit score. This may happen if you're new to credit, have
                    no open credit accounts, or have not used any credit
                    accounts recently. To establish a credit history, consider
                    using credit products and making on-time payments. Review
                    your score factor grades to learn about what impacts your
                    score. Also, don't forget to check your credit report
                    regularly.
                  </>
                }
              >
                <QuestionCircleOutlined
                  style={{ fontSize: '17px' }}
                  className="secondary_text_light "
                />
              </CSTooltip>
            </div>
          </FlexContainer>
        )}
    </div>
  );
};

export default CreditFactorChart;
